import { FormEvent } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { PROJECT_STATUSES, STATUS_LABELS } from "@/lib/project-status"
import {
  CUSTOMER_TYPES,
  CUSTOMER_TYPE_LABELS,
  CUSTOMER_TYPE_SECTIONS,
  type CustomerType,
} from "@/lib/customer-types"
import type { PageProps } from "@/types/inertia"

export type CustomerOption = {
  id: number
  name: string
  customer_type: CustomerType
  communities: { id: number; name: string }[]
}

export type ProjectFormValues = {
  lot_number: string | null
  plan_name: string | null
  elevation: string | null
  address: string | null
  notes: string | null
  status: string
  customer_id: number | null
  community_id: number | null
  // Material-schedule header fields (see MATERIAL_SCHEDULE below).
  building_type?: string | null
  work_type?: string | null
  gate_code?: string | null
  project_manager?: string | null
  customer_phone?: string | null
  unit_apartment?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  subfloor?: string | null
  schedule_comments?: string | null
}

// FloorCore's own option-set spellings, verified against the live ERP — the
// eventual export is a straight field copy, and the schedule prints these
// strings verbatim rather than mapping a key to a label.
const BUILDING_TYPES = [
  "Single Family",
  "Townhouse",
  "Apartment",
  "Condo",
  "Commercial",
  "Highrise",
]
const WORK_TYPES = ["New Construction", "Retail", "Renovation"]

type Props = {
  customers: CustomerOption[]
  action: string
  method: "post" | "patch"
  submitLabel: string
  initial?: ProjectFormValues
  /**
   * Show the material-schedule header fields. Off for the quick-create form:
   * a gate code and a superintendent's phone are things a PC fills in once
   * the lot is real, not things that should stand between them and creating it.
   */
  showScheduleFields?: boolean
}

// Customer/community are chosen via selects whose "+ New…" options reveal
// inputs — inline creation, matching the backend's customer_choice /
// community_choice contract (ProjectsController#resolve_customer).
// Production builders require a community; every other customer type treats
// it as optional.
export function ProjectForm({
  customers,
  action,
  method,
  submitLabel,
  initial,
  showScheduleFields = false,
}: Props) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const form = useForm({
    customer_choice: initial?.customer_id ? String(initial.customer_id) : "one_off",
    customer_name: "",
    customer_type: "production_builder" as CustomerType,
    community_choice: initial?.community_id ? String(initial.community_id) : "",
    community_name: "",
    lot_number: initial?.lot_number ?? "",
    plan_name: initial?.plan_name ?? "",
    elevation: initial?.elevation ?? "",
    address: initial?.address ?? "",
    status: initial?.status ?? "measuring",
    notes: initial?.notes ?? "",
    building_type: initial?.building_type ?? "",
    work_type: initial?.work_type ?? "",
    gate_code: initial?.gate_code ?? "",
    project_manager: initial?.project_manager ?? "",
    customer_phone: initial?.customer_phone ?? "",
    unit_apartment: initial?.unit_apartment ?? "",
    city: initial?.city ?? "",
    state: initial?.state ?? "",
    postal_code: initial?.postal_code ?? "",
    subfloor: initial?.subfloor ?? "unspecified",
    schedule_comments: initial?.schedule_comments ?? "",
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (method === "post") form.post(action)
    else form.patch(action)
  }

  const selectedCustomer =
    form.data.customer_choice !== "one_off" && form.data.customer_choice !== "new"
      ? customers.find((c) => String(c.id) === form.data.customer_choice)
      : undefined
  const creatingCustomer = form.data.customer_choice === "new"
  const productionSelected = selectedCustomer?.customer_type === "production_builder"
  const creatingProduction = creatingCustomer && form.data.customer_type === "production_builder"

  const typesPresent = CUSTOMER_TYPES.filter((t) =>
    customers.some((c) => c.customer_type === t),
  )

  const changeCustomer = (value: string) => {
    form.setData((data) => ({
      ...data,
      customer_choice: value,
      customer_name: "",
      // A new customer always goes through the community-name input path;
      // for existing customers the community select starts empty ("none" for
      // non-production, "choose one" for production).
      community_choice: value === "new" ? "new" : "",
      community_name: "",
    }))
  }

  return (
    <form onSubmit={submit} className="max-w-md space-y-4">
      <div className="space-y-2">
        <label htmlFor="customer_choice">Customer</label>
        <Select
          id="customer_choice"
          value={form.data.customer_choice}
          onChange={(e) => changeCustomer(e.target.value)}
        >
          <option value="one_off">One-off project (no customer)</option>
          {typesPresent.map((type) => (
            <optgroup key={type} label={CUSTOMER_TYPE_SECTIONS[type]}>
              {customers
                .filter((c) => c.customer_type === type)
                .map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name}
                  </option>
                ))}
            </optgroup>
          ))}
          <option value="new">+ New customer…</option>
        </Select>
      </div>

      {creatingCustomer && (
        <>
          <div className="space-y-2">
            <label htmlFor="customer_type">Customer type</label>
            <Select
              id="customer_type"
              value={form.data.customer_type}
              onChange={(e) =>
                form.setData("customer_type", e.target.value as CustomerType)
              }
            >
              {CUSTOMER_TYPES.map((type) => (
                <option key={type} value={type}>
                  {CUSTOMER_TYPE_LABELS[type]}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-2">
            <label htmlFor="customer_name">Customer name</label>
            <Input
              id="customer_name"
              type="text"
              required
              placeholder={creatingProduction ? "Pulte" : "Jane Smith"}
              aria-invalid={!!errors.customer_name}
              value={form.data.customer_name}
              onChange={(e) => form.setData("customer_name", e.target.value)}
            />
            {errors.customer_name && (
              <p className="text-xs text-danger-display">{errors.customer_name}</p>
            )}
          </div>
        </>
      )}

      {selectedCustomer && (
        <div className="space-y-2">
          <label htmlFor="community_choice">
            {productionSelected ? "Community" : "Community (optional)"}
          </label>
          <Select
            id="community_choice"
            required={productionSelected}
            value={form.data.community_choice}
            onChange={(e) =>
              form.setData((data) => ({
                ...data,
                community_choice: e.target.value,
                community_name: "",
              }))
            }
          >
            {productionSelected ? (
              <option value="" disabled>
                Choose a community…
              </option>
            ) : (
              <option value="">No community</option>
            )}
            {selectedCustomer.communities.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
            <option value="new">+ New community…</option>
          </Select>
          {errors.community_choice && (
            <p className="text-xs text-danger-display">{errors.community_choice}</p>
          )}
        </div>
      )}

      {(creatingCustomer || form.data.community_choice === "new") &&
        form.data.customer_choice !== "one_off" && (
          <div className="space-y-2">
            <label htmlFor="community_name">
              {creatingCustomer && !creatingProduction
                ? "Community (optional)"
                : "Community name"}
            </label>
            <Input
              id="community_name"
              type="text"
              required={!creatingCustomer || creatingProduction}
              placeholder="Crescent Cove"
              aria-invalid={!!errors.community_name}
              value={form.data.community_name}
              onChange={(e) => form.setData("community_name", e.target.value)}
            />
            {errors.community_name && (
              <p className="text-xs text-danger-display">{errors.community_name}</p>
            )}
          </div>
        )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="lot_number">Lot #</label>
          <Input
            id="lot_number"
            type="text"
            placeholder="03300"
            aria-invalid={!!errors.lot_number}
            value={form.data.lot_number}
            onChange={(e) => form.setData("lot_number", e.target.value)}
          />
          {errors.lot_number && (
            <p className="text-xs text-danger-display">{errors.lot_number}</p>
          )}
        </div>
        <div className="space-y-2">
          <label htmlFor="elevation">Elevation</label>
          <Input
            id="elevation"
            type="text"
            placeholder="B"
            aria-invalid={!!errors.elevation}
            value={form.data.elevation}
            onChange={(e) => form.setData("elevation", e.target.value)}
          />
          {errors.elevation && (
            <p className="text-xs text-danger-display">{errors.elevation}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="plan_name">Plan name</label>
        <Input
          id="plan_name"
          type="text"
          placeholder="Renown"
          aria-invalid={!!errors.plan_name}
          value={form.data.plan_name}
          onChange={(e) => form.setData("plan_name", e.target.value)}
        />
        {errors.plan_name && (
          <p className="text-xs text-danger-display">{errors.plan_name}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="address">Address</label>
        <Input
          id="address"
          type="text"
          placeholder="412 Maple St"
          aria-invalid={!!errors.address}
          value={form.data.address}
          onChange={(e) => form.setData("address", e.target.value)}
        />
        {errors.address && (
          <p className="text-xs text-danger-display">{errors.address}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="status">Status</label>
        <Select
          id="status"
          value={form.data.status}
          onChange={(e) => form.setData("status", e.target.value)}
        >
          {PROJECT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {STATUS_LABELS[status]}
            </option>
          ))}
        </Select>
        {errors.status && (
          <p className="text-xs text-danger-display">{errors.status}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="notes">Notes</label>
        <Textarea
          id="notes"
          rows={4}
          placeholder="Internal — never printed on the material schedule"
          aria-invalid={!!errors.notes}
          value={form.data.notes}
          onChange={(e) => form.setData("notes", e.target.value)}
        />
        {errors.notes && (
          <p className="text-xs text-danger-display">{errors.notes}</p>
        )}
      </div>

      {showScheduleFields && (
        <>
          <div className="border-t border-hairline pt-4">
            <h2>Material schedule</h2>
            <p className="text-ink-muted">
              These print in the header block of the schedule the field team works from.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="building_type">Building type</label>
              <Select
                id="building_type"
                value={form.data.building_type}
                onChange={(e) => form.setData("building_type", e.target.value)}
              >
                <option value="">Not set</option>
                {BUILDING_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <label htmlFor="work_type">Work type</label>
              <Select
                id="work_type"
                value={form.data.work_type}
                onChange={(e) => form.setData("work_type", e.target.value)}
              >
                <option value="">Not set</option>
                {WORK_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="gate_code">Gate code</label>
              <Input
                id="gate_code"
                type="text"
                placeholder="#4821"
                value={form.data.gate_code}
                onChange={(e) => form.setData("gate_code", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="unit_apartment">Unit / apartment</label>
              <Input
                id="unit_apartment"
                type="text"
                placeholder="4910-165"
                value={form.data.unit_apartment}
                onChange={(e) => form.setData("unit_apartment", e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="project_manager">Project manager</label>
              <Input
                id="project_manager"
                type="text"
                placeholder="Jimmy Higgins"
                value={form.data.project_manager}
                onChange={(e) => form.setData("project_manager", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="customer_phone">Customer phone</label>
              <Input
                id="customer_phone"
                type="tel"
                placeholder="(843) 555-0134"
                value={form.data.customer_phone}
                onChange={(e) => form.setData("customer_phone", e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-[2fr_1fr_1fr] gap-4">
            <div className="space-y-2">
              <label htmlFor="city">City</label>
              <Input
                id="city"
                type="text"
                placeholder="Myrtle Beach"
                value={form.data.city}
                onChange={(e) => form.setData("city", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="state">State</label>
              <Input
                id="state"
                type="text"
                placeholder="SC"
                value={form.data.state}
                onChange={(e) => form.setData("state", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="postal_code">Zip</label>
              <Input
                id="postal_code"
                type="text"
                placeholder="29577"
                value={form.data.postal_code}
                onChange={(e) => form.setData("postal_code", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="subfloor">Subfloor default</label>
            <Select
              id="subfloor"
              value={form.data.subfloor}
              onChange={(e) => form.setData("subfloor", e.target.value)}
            >
              <option value="unspecified">Unspecified</option>
              <option value="concrete">Concrete</option>
              <option value="wood">Wood</option>
            </Select>
            <p className="text-xs text-ink-muted">
              Each cropped floor plan can override this, and each room can override that.
            </p>
          </div>

          <div className="space-y-2">
            <label htmlFor="schedule_comments">Schedule comments</label>
            <Textarea
              id="schedule_comments"
              rows={4}
              placeholder="Prints at the bottom of the schedule — e.g. “1/8 spacers”, “shower curbs get matching wall tile”."
              value={form.data.schedule_comments}
              onChange={(e) => form.setData("schedule_comments", e.target.value)}
            />
          </div>
        </>
      )}

      {errors.base && <p className="text-xs text-danger-display">{errors.base}</p>}

      <Button type="submit" disabled={form.processing}>
        {submitLabel}
      </Button>
    </form>
  )
}
